import { useSyncExternalStore } from 'react';
import type { AccountKeys, MasterKey } from '@realmery/vault';
import { closeRealms } from '../db/client.ts';
import { accessFor, rememberUser, rememberedUser, type Access } from './access.ts';
import { AccountError, api, type Me, type Wrap } from './api.ts';
import { deviceUnlock, forgetDevice, forgetEverything, keepUnlocked, saveOfflineWraps } from './device.ts';
import { forgetSharingKeys, sharingKeys } from './sharingKeys.ts';

/**
 * Who is signed in, and the unlocked master key. The key is held only in this module's
 * memory, so reloading the page locks the realms again (docs/REALM-ENCRYPTION.md section
 * 4) - unless the owner chose to keep this device unlocked (device.ts).
 */
export type AccountState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; me: Me; master: MasterKey | null };

let state: AccountState = { status: 'loading' };
/** The unlocked master key and whose it is; also kept while offline. */
let held: { userId: string; master: MasterKey } | null = null;
const listeners = new Set<() => void>();
/** When this page last refreshed the offline wraps, and for whom. */
let wrapsSaved: { userId: string; at: number } | null = null;

/**
 * Keeps the device's copies of the passkey and recovery wraps current, so they can unlock
 * offline (device.ts). At most once a minute; changes on the account page force it.
 */
export async function refreshOfflineWraps(userId: string, force = false): Promise<void> {
  if (!force && wrapsSaved?.userId === userId && Date.now() - wrapsSaved.at < 60_000) return;
  wrapsSaved = { userId, at: Date.now() };
  try {
    const { wraps } = await api.get<{ wraps: Wrap[] }>('account/vault');
    if (state.status === 'signed-in' && state.me.user.id === userId) await saveOfflineWraps(userId, wraps);
  } catch {
    wrapsSaved = null; // try again next time
  }
}

function masterOf(userId: string): MasterKey | null {
  return held?.userId === userId ? held.master : null;
}

function set(next: AccountState): void {
  state = next;
  // Remembered so this account's realms still open offline (access.ts); forgotten on sign-out.
  if (next.status === 'signed-in') {
    rememberUser({ id: next.me.user.id, email: next.me.user.email });
    if (held && held.userId !== next.me.user.id) held = null;
    void refreshOfflineWraps(next.me.user.id);
  }
  if (next.status === 'signed-out') {
    rememberUser(null);
    held = null;
    forgetSharingKeys();
    wrapsSaved = null;
    void forgetEverything();
    closeRealms();
  }
  for (const listener of listeners) listener();
}

export function accountState(): AccountState {
  return state;
}

export function useAccount(): AccountState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

/** Reloads the signed-in user, keeping the unlocked key if it is the same user. */
export async function refreshAccount(): Promise<AccountState> {
  try {
    const me = await api.get<Me>('account/me');
    set({ status: 'signed-in', me, master: masterOf(me.user.id) });
  } catch (error) {
    if (error instanceof AccountError && error.code === 'signed_out') set({ status: 'signed-out' });
    else if (error instanceof AccountError && error.code === 'unavailable') set({ status: 'unavailable' });
    else if (state.status === 'loading') set({ status: 'unavailable' });
    else throw error;
  }
  return state;
}

/** Whether realm pages may open: checks the session once per page load, then follows sign-in and sign-out. */
export async function currentAccess(): Promise<Access> {
  if (state.status === 'loading') await refreshAccount().catch(() => undefined);
  const session =
    state.status === 'signed-in'
      ? { status: 'signed-in' as const, user: { id: state.me.user.id, email: state.me.user.email } }
      : state.status === 'signed-out'
        ? { status: 'signed-out' as const }
        : { status: 'unavailable' as const };
  return accessFor(session, rememberedUser());
}

/**
 * The key that opens this account's realms: the master key in memory, or else the one a
 * kept-unlocked device holds. Null means the realms are locked.
 */
export async function realmsKeyOf(userId: string): Promise<CryptoKey | null> {
  let master = masterOf(userId);
  if (!master) {
    master = await deviceUnlock(userId);
    if (master) hold(userId, master);
  }
  return master ? master.deriveKey('realms') : null;
}

/** The signed-in account's id and sharing keys, for sharing realms; refused while locked or signed out. */
export async function mySharingKeys(): Promise<{ userId: string; keys: AccountKeys }> {
  const userId = state.status === 'signed-in' ? state.me.user.id : null;
  const master = userId ? masterOf(userId) : null;
  if (!userId || !master) throw new AccountError('locked', 'Sign in and unlock to share realms.');
  return { userId, keys: await sharingKeys(userId, master) };
}

function hold(userId: string, master: MasterKey): void {
  held = { userId, master };
  // Makes the account's sharing keys the first time, or opens them; a failure (offline) waits for the next unlock.
  sharingKeys(userId, master).catch((error) => console.warn('Could not prepare the sharing keys', error));
  if (state.status === 'signed-in' && state.me.user.id === userId) set({ ...state, master });
  else for (const listener of listeners) listener();
}

/** After an unlock: holds the key, and keeps this device unlocked if the owner asked. */
async function keep(userId: string, master: MasterKey, keepOnDevice: boolean): Promise<void> {
  if (keepOnDevice) await keepUnlocked(userId, master).catch((error) => console.warn('Could not keep this device unlocked', error));
  hold(userId, master);
}

export async function unlocked(master: MasterKey, keepOnDevice = false): Promise<void> {
  if (state.status === 'signed-in') await keep(state.me.user.id, master, keepOnDevice);
}

/** After an offline unlock (flows.ts), for the account remembered on this device. */
export async function unlockedOffline(userId: string, master: MasterKey, keepOnDevice = false): Promise<void> {
  await keep(userId, master, keepOnDevice);
}

/** After a sign-in. A passkey without PRF signs in without a key, and keeps one already held. */
export async function signedIn(master: MasterKey | null, keepOnDevice = false): Promise<void> {
  const me = await api.get<Me>('account/me');
  if (master) await keep(me.user.id, master, keepOnDevice);
  set({ status: 'signed-in', me, master: masterOf(me.user.id) });
}

/**
 * "Lock this device": forgets the device's key and locks the realms now. The offline
 * wraps stay: they still need a passkey or the recovery key.
 */
export async function lockDevice(): Promise<void> {
  await forgetDevice();
  held = null;
  forgetSharingKeys();
  closeRealms();
  if (state.status === 'signed-in') set({ ...state, master: null });
}

export async function signOut(): Promise<void> {
  try {
    await api.post('signin/logout');
  } finally {
    set({ status: 'signed-out' });
  }
}

export function lockedOut(): void {
  set({ status: 'signed-out' });
}
